/**
 * AutoFitTextarea — a textarea that always shows all of its content.
 *
 * WHY THIS EXISTS (prod bug, 2026-08-17)
 * --------------------------------------
 * The estimate line-item description grew itself via an `onInput` handler.
 * `onInput` fires only while a human types, so a row that MOUNTED with a saved
 * description rendered at one row — and because the field also carried
 * `overflow: hidden`, the clipped text could not even be scrolled to. Dragging
 * the resize handle was the only way to read your own data.
 *
 * The fix is to size in a layout effect keyed on `value`, so the height is
 * correct on FIRST PAINT, and to own `overflowY` so text is never unreachable:
 * hidden while the content fits, `auto` once it hits the row cap.
 *
 * Safe inside <AutoHeight> — that shell is ResizeObserver-driven, so it
 * re-measures when this field grows rather than clipping it.
 */
import { useRef, useEffect, useLayoutEffect, useCallback, forwardRef } from 'react';
import { proseFieldProps } from '../../utils/ui/textEntryProps';

/** Cap growth so one long field can't push a form's actions off screen. */
export const DEFAULT_MAX_ROWS = 10;

const numOr = (raw, fallback) => {
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Size a textarea to fit its content, capped at `maxRows`; scrolls past the cap.
 *
 * Measurements come from the element's OWN computed style rather than a
 * hardcoded row height, because callers render this at different font sizes,
 * line heights, and padding (desktop table cell vs mobile card vs modal).
 */
export function fitTextareaHeight(el, maxRows = DEFAULT_MAX_ROWS) {
  if (!el) return;
  const cs = typeof window !== 'undefined' && window.getComputedStyle ? window.getComputedStyle(el) : null;
  const fontPx = numOr(cs?.fontSize, 14);
  const lineHeight = numOr(cs?.lineHeight, fontPx * 1.45);
  const padY = numOr(cs?.paddingTop, 0) + numOr(cs?.paddingBottom, 0);
  const borderY = numOr(cs?.borderTopWidth, 0) + numOr(cs?.borderBottomWidth, 0);

  el.style.height = 'auto';
  // jsdom has no layout engine (scrollHeight is always 0). Bail rather than
  // collapse the field to nothing in tests.
  if (!el.scrollHeight) {
    el.style.height = '';
    return;
  }
  // scrollHeight excludes the border, but `box-sizing: border-box` means the
  // height we set includes it — without this the last line is clipped by ~2px.
  const needed = el.scrollHeight + borderY;
  const max = Math.round(lineHeight * maxRows + padY + borderY);
  el.style.height = `${Math.min(needed, max)}px`;
  el.style.overflowY = needed > max ? 'auto' : 'hidden';
}

/**
 * Drop-in replacement for `<textarea>` on any free-prose field.
 *
 * Pass `minHeight` through `style` as usual — it is respected as the floor,
 * since the browser applies it over the computed height.
 */
const AutoFitTextarea = forwardRef(function AutoFitTextarea(
  { value, maxRows = DEFAULT_MAX_ROWS, style, onInput, ...rest },
  forwardedRef,
) {
  const innerRef = useRef(null);

  // Callers keep their own ref (LogJob focuses its notes field, AiQuickLogCard
  // measures its own) — merge rather than clobber, or their ref reads null.
  const setRefs = useCallback((node) => {
    innerRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);

  // Keyed on `value` so it is right on mount AND whenever the field is
  // repopulated from outside (loading a saved draft, resetting a form).
  useLayoutEffect(() => {
    fitTextareaHeight(innerRef.current, maxRows);
  }, [value, maxRows]);

  // A width change re-wraps the text, which changes the line count.
  useEffect(() => {
    const onResize = () => fitTextareaHeight(innerRef.current, maxRows);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [maxRows]);

  return (
    <textarea
      {...proseFieldProps}
      {...rest}
      ref={setRefs}
      value={value}
      // Browsers default <textarea> to MONOSPACE — every caller that forgot an
      // explicit fontFamily shipped typewriter placeholders (the estimate
      // scope field). Inherit the app font as the floor; caller styles spread
      // after and still win.
      style={{ fontFamily: 'inherit', ...style }}
      onInput={e => {
        // Resize immediately rather than waiting on the parent's re-render, in
        // case a caller throttles or defers its onChange.
        fitTextareaHeight(e.target, maxRows);
        if (onInput) onInput(e);
      }}
    />
  );
});

export default AutoFitTextarea;
